'use client';

import { type ChangeEvent, cloneElement, type ReactElement } from 'react';
import {
  Controller,
  type FieldValues,
  type UseControllerProps,
  useFormContext,
} from 'react-hook-form';

import { FieldErrorMessage } from '../field-error-message/field-error-message';
import { type InputProps } from '../form-input/form-input';
import SvgErrorIcon from '../svgs/error-icon';

import styles from './field-controller.module.scss';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type Transform<TElement = any> = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  input: (value: any) => any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  output: (event: ChangeEvent<TElement>) => any;
};

export type FieldControllerProps<T extends FieldValues> = UseControllerProps<T> & {
  children: ReactElement<InputProps>;
  dataTestId?: string;
  displayErrorIcon?: boolean;
  displayLabel?: boolean;
  displayMaxLengthCounter?: boolean;
  hint?: string;
  id?: string;
  isRequired?: boolean;
  label: string;
  maxLength?: number | undefined;
  name: string;
  optionalLabel?: string;
  transform?: Transform;
  type?: string;
};

export const FieldController = <T extends FieldValues>({
  children,
  dataTestId,
  displayErrorIcon = true,
  displayLabel = true,
  displayMaxLengthCounter,
  hint,
  id,
  isRequired = false,
  label,
  maxLength,
  name,
  optionalLabel,
  rules,
  transform,
  type = 'text',
  ...rest
}: FieldControllerProps<T>) => {
  const { control } = useFormContext<T>();

  const isRequiredOrHasRequiredRule = isRequired || Boolean(rules?.required);
  const errorMessageId = `${name || id}-error`;
  const showOptionalLabel = !isRequiredOrHasRequiredRule && !children.props?.disabled;

  return (
    <div className={styles.field} data-testid={dataTestId ?? name ?? id}>
      <Controller
        control={control}
        name={name}
        render={({ field, fieldState: { error } }) => {
          const inputProps: InputProps & typeof field = {
            ...field,
            errorMessageId,
            hasError: Boolean(error),
            id: name || id,
            isRequired: isRequiredOrHasRequiredRule,
            onBlur: () => {
              if (typeof field.value === 'string' && field.value !== field.value.trimEnd()) {
                field.onChange(field.value.trimEnd());
              }

              field.onBlur();
            },
            onChange: (value: unknown) => {
              field.onChange(typeof value === 'string' ? value.trimStart() : value);
            },
            transform,
            type,
          };

          if (maxLength !== undefined) {
            inputProps.maxLength = maxLength;
          }

          return (
            <>
              {cloneElement(children, inputProps)}
              {displayLabel && (
                <label className={styles['field__label']} htmlFor={name || id}>
                  {label} {showOptionalLabel && optionalLabel && `(${optionalLabel})`}
                </label>
              )}
              {!error && hint && <p className={styles['field__hint']}>{hint}</p>}
              {!error && !hint && maxLength && displayMaxLengthCounter && (
                <p className={styles['field__hint']}>
                  {field.value?.toString().length ?? 0}/{maxLength}
                </p>
              )}
              {error?.message && displayErrorIcon && (
                <SvgErrorIcon className={styles['field__error-icon']} />
              )}
              {error?.message && <FieldErrorMessage id={errorMessageId} message={error.message} />}
            </>
          );
        }}
        rules={rules}
        {...rest}
      />
    </div>
  );
};
